import { ComponentProps, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Layer, type MapLayerMouseEvent } from 'react-map-gl';
import { Feature, Point } from 'geojson';
import { useGroupHover } from '@alltrails/core';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { MapMouseEvent, MapTouchEvent } from 'mapbox-gl';
import { TrailActivityType } from '@alltrails/trails';
import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import TrailCardLocation from '@alltrails/amplitude/enums/TrailCardLocation';
import logExploreMapPinSelected from '@alltrails/amplitude/events/logExploreMapPinSelected';
import { isGreaterThanZoomExpression } from '../../utils/constants';
import { defaultPinLayerLayoutProps, defaultSymbolLayerLayoutProps, getLayerBeforeId, trailPinsId } from '../../utils/layers';
import { useDispatch, useSelector } from '../../redux';
import useMap from '../../hooks/useMap';
import useSvgImages, { SvgDefinition } from '../../utils/useSvgImages';
import { pinPopupOffset } from '../../utils/svgDefinitions/svgMarkerComponents';
import { updateActiveTrails, updateCursor } from '../../redux/reducer';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';
import { coordinatesToSerializedCoordinates, serializedCoordinatesToCoordinates } from '../../utils/serializedCoordinates';
import createTrailPinSvg from '../../utils/svgDefinitions/createTrailPinSvg';
import TrailResultProperties from './TrailResultProperties';
import ActiveTrailPopup from './ActiveTrailPopup';

const trailPinImageName = 'trail-pin';
const trailPinActiveImageName = 'trail-pin-active';
const trailPinClosedImageName = 'trail-pin-closed';
const trailPinClosedActiveImageName = 'trail-pin-closed-active';
const multiTrailPinImageNameBase = 'trail-pin-';
const multiTrailPinActiveImageNameBase = 'trail-pin-active-';
const multiTrailPinClosedImageNameBase = 'trail-pin-closed-';
const multiTrailPinClosedActiveImageNameBase = 'trail-pin-closed-active-';
const multiTrailPinSavedImageNameBase = 'trail-pin-saved-';
const multiTrailPinSavedActiveImageNameBase = 'trail-pin-saved-active-';
const trailPinSavedImageName = 'trail-pin-saved';
const trailPinSavedActiveImageName = 'trail-pin-saved-active';
const isMultiTrailExpression = ['>', ['get', 'trailCount'], 1];
const isTrailClosedExpression = ['==', ['get', 'isClosed'], true];
const isTrailSavedExpression = ['==', ['get', 'isSaved'], true];

export type TrailPinsProps = {
  activityType?: TrailActivityType; // Used to display the correct trail card stats
  cardLocation?: TrailCardLocation;
  detailedCardLocation?: CardLocation;
  mapLocation?: string; // The shared util we use for save analytics takes old page strings rather than just the MapLocation enum :/
  sourceId: string;
  /**
   * When using a symbol layer with both icon AND text, all of the text renders on top of all of the icons in that layer.
   * So, if we try to apply the trail count as text for each multi-trail pin, we run into a bug where the counts render on top of other pins.
   * To fix this, we need to include the text in the actual image that we use for the icon, which requires us to generate these images dynamically.
   * trailCounts is the set of counts that we need for each pin in order to generate these images. closedTrailCounts is the same thing, but for closed trailheads.
   */
  trailCounts: Set<number>;
  closedTrailCounts: Set<number>;
  savedTrailCounts: Set<number>;
} & Pick<ComponentProps<typeof ActiveTrailPopup>, 'onTrailCardViewed'>;

const TrailPins = ({
  activityType,
  cardLocation,
  detailedCardLocation,
  mapLocation,
  onTrailCardViewed,
  sourceId,
  trailCounts,
  closedTrailCounts,
  savedTrailCounts
}: TrailPinsProps) => {
  const map = useMap();
  const isMobile = useIsMobileSizedScreen();
  const dispatch = useDispatch();

  const analyticsInfo = useMemo(
    () =>
      detailedCardLocation && mapLocation
        ? {
            detailedCardLocation,
            page: mapLocation
          }
        : undefined,
    [detailedCardLocation, mapLocation]
  );
  const { isComplete, isFavorite, isVerified, handleFavoriteClick, listModal, signUpModal } = useReduxListItems(undefined, undefined, analyticsInfo);
  const { activeTrails, allowClickEvents, trailResults } = useSelector(state => ({
    activeTrails: state.map.activeTrails,
    allowClickEvents: state.map.allowClickEvents,
    trailResults: state.map.trailResults
  }));

  const [hoveredSerializedCoordinates, setHoveredSerializedCoordinates] = useState('');
  const [selectedSerializedCoordinates, setSelectedSerializedCoordinates] = useState('');

  let lastHoveredTrailId = useRef<number | undefined | null>(null);
  const onHover = useCallback(
    (properties?: TrailResultProperties) => {
      // We don't bother passing in properties when a user hovers on the trail card, since no changes need to be dispatched in that case
      if (properties) {
        // We must use the serialized coordinates that we assigned to the point rather than constructing the string from the coordinates
        // on the mapbox event because for some (still unknown) reason, the mapbox coordinates were the slightest bit off...
        const serializedCoordinates = properties.serializedCoordinates;
        setHoveredSerializedCoordinates(serializedCoordinates);

        if (selectedSerializedCoordinates) {
          if (serializedCoordinates === selectedSerializedCoordinates) {
            return;
          } else {
            setSelectedSerializedCoordinates('');
          }
        }
        const hoveredTrails = trailResults?.[serializedCoordinates]?.filter(trail => Boolean(trail));
        dispatch(updateActiveTrails(hoveredTrails));
        dispatch(updateCursor('pointer'));

        if (hoveredTrails) {
          const trailId = hoveredTrails.length === 1 ? hoveredTrails[0].ID : undefined;
          const pinType = hoveredTrails.length === 1 ? 'trail' : 'trailhead';
          // This function gets called on mouse move so we want to only fire it once per trail pin
          if (lastHoveredTrailId.current !== trailId) {
            lastHoveredTrailId.current = trailId;
            // TODO DISCO-20382
            if (cardLocation === 'explore map view') {
              logExploreMapPinSelected({ action_taken: 'hovered on explore map', pin_type: pinType, trail_id: trailId });
            }
          }
        }
      }
    },
    [dispatch, selectedSerializedCoordinates, trailResults, cardLocation]
  );

  const onHoverEnd = useCallback(() => {
    setHoveredSerializedCoordinates('');
    // We don't want to clear activeTrails if a trail pin has been selected - that will get taken care of when the pin is de-selected
    if (!selectedSerializedCoordinates) {
      dispatch(updateActiveTrails(undefined));
    }
    dispatch(updateCursor('unset'));
  }, [dispatch, selectedSerializedCoordinates]);

  const { onMouseEnter, onMouseLeave } = useGroupHover(onHover, onHoverEnd, true);
  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);

  const onMouseMove = useCallback(
    (event: MapLayerMouseEvent) => {
      if (onMouseEnter && event.features) {
        const marker = event.features[0] as unknown as Feature<Point, TrailResultProperties>;
        onMouseEnter(marker.properties);
      }
    },
    [onMouseEnter]
  );

  const onClick = useCallback(
    (event: MapLayerMouseEvent) => {
      if (event.features) {
        event.preventDefault(); // Allows us to detect when a user clicks on the map but NOT on this layer
        const marker = event.features[0] as unknown as Feature<Point, TrailResultProperties>;
        if (trailResults) {
          const serializedCoordinates = marker.properties.serializedCoordinates;
          setSelectedSerializedCoordinates(serializedCoordinates);

          const trailsAtCoordinates = trailResults[serializedCoordinates];
          const selectedTrail = trailsAtCoordinates[0];
          if (selectedTrail) {
            dispatch(updateActiveTrails([selectedTrail]));

            const pinType = trailsAtCoordinates.length === 1 ? 'trail' : 'trailhead';
            // TODO DISCO-20382
            if (cardLocation === 'explore map view') {
              logExploreMapPinSelected({ action_taken: 'clicked on explore map', pin_type: pinType, trail_id: selectedTrail.ID });
            }
          }
        }
      }
    },
    [dispatch, trailResults, cardLocation]
  );

  useEffect(() => {
    if (!allowClickEvents) {
      return;
    }

    registerEventHandler(trailPinsId, 'click', onClick);
    !isMobile && registerEventHandler(trailPinsId, 'mousemove', onMouseMove);
    !isMobile && onMouseLeave && registerEventHandler(trailPinsId, 'mouseleave', onMouseLeave);
    return () => {
      unregisterEventHandler(trailPinsId, 'click', onClick);
      unregisterEventHandler(trailPinsId, 'mousemove', onMouseMove);
      onMouseLeave && unregisterEventHandler(trailPinsId, 'mouseleave', onMouseLeave);
    };
  }, [allowClickEvents, isMobile, onClick, onMouseLeave, onMouseMove, registerEventHandler, unregisterEventHandler]);

  const activeTrailSerializedCoordinates = useMemo(() => {
    if (!activeTrails?.length) {
      return undefined;
    }
    const trail = activeTrails[0];
    if (!trail) {
      return undefined;
    }

    const coordinates = trail._cluster_geoloc ?? trail._geoloc;
    if (!coordinates) {
      return undefined;
    }

    return coordinatesToSerializedCoordinates(coordinates);
  }, [activeTrails]);

  useEffect(() => {
    if (activeTrailSerializedCoordinates !== selectedSerializedCoordinates) {
      setSelectedSerializedCoordinates('');
    }
    // If the active trail coordinates are changed by another component, clear the selected state
    // since that can remain while a user hovers over something like a trail card.
    // Don't want to trigger this when the selected coordinates change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTrailSerializedCoordinates]);

  useEffect(() => {
    const onClick = (event: MapMouseEvent | MapTouchEvent) => {
      // We prevent default when a user clicks on this layer
      if (!event.defaultPrevented) {
        setSelectedSerializedCoordinates('');
        dispatch(updateActiveTrails(undefined));
      }
    };

    map?.on('click', onClick);
    return () => {
      map?.off('click', onClick);
    };
  }, [dispatch, map]);

  // Users can press shift to hide the popup
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.shiftKey) {
        setIsShiftPressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.shiftKey) {
        setIsShiftPressed(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const popup = useMemo(() => {
    if (!trailResults || activeTrails?.length !== 1 || !activeTrailSerializedCoordinates) {
      return null;
    }
    if (isShiftPressed) {
      return null;
    }

    // When the active trail is part of a selected multi trailhead, we show the carousel popup - otherwise, just show the trail card
    const trailsAtActiveTrailhead = trailResults[activeTrailSerializedCoordinates];
    const isSelectedMultiTrail = selectedSerializedCoordinates === activeTrailSerializedCoordinates && trailsAtActiveTrailhead?.length > 1;
    const initialTrailIndex = isSelectedMultiTrail ? trailsAtActiveTrailhead.findIndex(({ ID }) => ID === activeTrails[0].ID) : undefined;
    const trailsInPopup = isSelectedMultiTrail ? trailsAtActiveTrailhead : activeTrails;

    const { lat, lng } = serializedCoordinatesToCoordinates(activeTrailSerializedCoordinates);
    return (
      <ActiveTrailPopup
        activityType={activityType}
        cardLocation={cardLocation}
        detailedCardLocation={detailedCardLocation}
        initialTrailIndex={initialTrailIndex}
        isComplete={id => isComplete('trail', id)}
        isFavorite={id => isFavorite('trail', id)}
        isVerified={id => isVerified('trail', id)}
        latitude={lat}
        longitude={lng}
        offset={pinPopupOffset}
        onFavoriteClick={id => handleFavoriteClick('trail', id)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onTrailCardViewed={onTrailCardViewed}
        onTrailChange={trail => {
          dispatch(updateActiveTrails(trail ? [trail] : undefined));
        }}
        trails={trailsInPopup}
      />
    );
  }, [
    activeTrailSerializedCoordinates,
    activeTrails,
    activityType,
    cardLocation,
    detailedCardLocation,
    dispatch,
    handleFavoriteClick,
    isShiftPressed,
    isComplete,
    isFavorite,
    isVerified,
    onMouseEnter,
    onMouseLeave,
    onTrailCardViewed,
    selectedSerializedCoordinates,
    trailResults
  ]);

  const svgDefinitions = useMemo(() => {
    const definitions: SvgDefinition[] = [
      createTrailPinSvg(trailPinImageName, 1, false),
      createTrailPinSvg(trailPinActiveImageName, 1, true),
      createTrailPinSvg(trailPinClosedImageName, 1, false, true),
      createTrailPinSvg(trailPinClosedActiveImageName, 1, true, true),
      createTrailPinSvg(trailPinSavedImageName, 1, false, false, false, true),
      createTrailPinSvg(trailPinSavedActiveImageName, 1, true, false, false, true)
    ];
    trailCounts.forEach(count => {
      definitions.push(
        createTrailPinSvg(`${multiTrailPinImageNameBase}${count}`, count, false),
        createTrailPinSvg(`${multiTrailPinActiveImageNameBase}${count}`, count, true)
      );
    });
    closedTrailCounts.forEach(count => {
      definitions.push(
        createTrailPinSvg(`${multiTrailPinClosedImageNameBase}${count}`, count, false, true),
        createTrailPinSvg(`${multiTrailPinClosedActiveImageNameBase}${count}`, count, true, true)
      );
    });
    savedTrailCounts.forEach(count => {
      definitions.push(
        createTrailPinSvg(`${multiTrailPinSavedImageNameBase}${count}`, count, false, false, false, true),
        createTrailPinSvg(`${multiTrailPinSavedActiveImageNameBase}${count}`, count, true, false, false, true)
      );
    });
    return definitions;
  }, [trailCounts, closedTrailCounts, savedTrailCounts]);

  const imagesAreLoaded = useSvgImages(svgDefinitions);

  if (!imagesAreLoaded) {
    return null;
  }

  const isHoveredExpression = ['==', ['get', 'serializedCoordinates'], hoveredSerializedCoordinates || ''];
  const isSelectedExpression = ['==', ['get', 'serializedCoordinates'], selectedSerializedCoordinates || ''];

  // This is really just for the case where some other component sets an active trail.
  // We want to add highlight styling to the pin to draw attention to the pin location on the map.
  const isActiveTrailExpression = ['==', ['get', 'serializedCoordinates'], activeTrailSerializedCoordinates || ''];

  return (
    <>
      <Layer
        source={sourceId}
        type="symbol"
        id={trailPinsId}
        beforeId={getLayerBeforeId(map, trailPinsId)}
        filter={['!', ['has', 'point_count']]}
        layout={{
          ...defaultSymbolLayerLayoutProps,
          ...defaultPinLayerLayoutProps,
          'symbol-sort-key': ['case', ['any', isHoveredExpression, isSelectedExpression], 1, 0],
          'icon-allow-overlap': isGreaterThanZoomExpression,
          'icon-image': [
            'case',
            // Selected, closed multi-trail
            ['all', isSelectedExpression, isMultiTrailExpression, isTrailClosedExpression],
            ['concat', multiTrailPinClosedActiveImageNameBase, ['get', 'trailCount']],
            // Hovered, closed multi-trail
            ['all', isHoveredExpression, isMultiTrailExpression, isTrailClosedExpression],
            ['concat', multiTrailPinClosedActiveImageNameBase, ['get', 'trailCount']],
            // Active, closed multi-trail
            ['all', isActiveTrailExpression, isMultiTrailExpression, isTrailClosedExpression],
            ['concat', multiTrailPinClosedActiveImageNameBase, ['get', 'trailCount']],
            // Default, closed multi-trail
            ['all', isMultiTrailExpression, isTrailClosedExpression],
            ['concat', multiTrailPinClosedImageNameBase, ['get', 'trailCount']],
            // Saved multi-trails:
            // Selected, saved multi-trail
            ['all', isSelectedExpression, isMultiTrailExpression, isTrailSavedExpression],
            ['concat', multiTrailPinSavedActiveImageNameBase, ['get', 'trailCount']],
            // Hovered, saved multi-trail
            ['all', isHoveredExpression, isMultiTrailExpression, isTrailSavedExpression],
            ['concat', multiTrailPinSavedActiveImageNameBase, ['get', 'trailCount']],
            // Active, saved multi-trail
            ['all', isActiveTrailExpression, isMultiTrailExpression, isTrailSavedExpression],
            ['concat', multiTrailPinSavedActiveImageNameBase, ['get', 'trailCount']],
            // Default, saved multi-trail
            ['all', isMultiTrailExpression, isTrailSavedExpression],
            ['concat', multiTrailPinSavedImageNameBase, ['get', 'trailCount']],
            // Selected multi-trail
            ['all', isSelectedExpression, isMultiTrailExpression],
            ['concat', multiTrailPinActiveImageNameBase, ['get', 'trailCount']],
            // Hovered multi-trail
            ['all', isHoveredExpression, isMultiTrailExpression],
            ['concat', multiTrailPinActiveImageNameBase, ['get', 'trailCount']],
            // Active multi-trail
            ['all', isActiveTrailExpression, isMultiTrailExpression],
            ['concat', multiTrailPinActiveImageNameBase, ['get', 'trailCount']],
            // Default multi-trail
            isMultiTrailExpression,
            ['concat', multiTrailPinImageNameBase, ['get', 'trailCount']],
            // Selected, closed single-trail
            ['all', isSelectedExpression, isTrailClosedExpression],
            trailPinClosedActiveImageName,
            // Hovered, closed single-trail
            ['all', isHoveredExpression, isTrailClosedExpression],
            trailPinClosedActiveImageName,
            // Active, closed single-trail
            ['all', isActiveTrailExpression, isTrailClosedExpression],
            trailPinClosedActiveImageName,
            // Default, closed single-trail
            isTrailClosedExpression,
            trailPinClosedImageName,
            // Saved single trails:
            // Selected, saved single-trail
            ['all', isSelectedExpression, isTrailSavedExpression],
            trailPinSavedActiveImageName,
            // Hovered, saved single-trail
            ['all', isHoveredExpression, isTrailSavedExpression],
            trailPinSavedActiveImageName,
            // Active, saved single-trail
            ['all', isActiveTrailExpression, isTrailSavedExpression],
            trailPinSavedActiveImageName,
            // Default, saved single-trail
            isTrailSavedExpression,
            trailPinSavedImageName,
            // Selected single-trail
            isSelectedExpression,
            trailPinActiveImageName,
            // Hovered single-trail
            isHoveredExpression,
            trailPinActiveImageName,
            // Active single-trail
            isActiveTrailExpression,
            trailPinActiveImageName,
            // Default single-trail
            trailPinImageName
          ]
        }}
      />
      {popup}
      {listModal}
      {signUpModal}
    </>
  );
};

export default TrailPins;
